import {
  ChecklistProgress,
  ChecklistProgressSteps,
  ChecklistStratum,
  ChecklistStep,
  ChecklistStepID,
  ChecklistSteps,
  ChecklistDocs,
} from './checklist.types'
import { getChecklistSteps } from './checklist.steps'

import { appNotifications } from '@/services/notification.app'
import { Mixpanel } from '@/analytics/mixpanel'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { router } from '@/helpers/router'

export class ChecklistStepView implements ChecklistStep {
  readonly id: ChecklistStepID
  readonly title: string
  readonly onClick: (
    self: ChecklistStepView,
    checklistDocs: ChecklistDocs,
  ) => void
  readonly targetProgress: number
  progress: number

  /** The parent Checklist instance. */
  checklist?: ChecklistView

  constructor(config: ChecklistStep) {
    this.id = config.id
    this.title = config.title
    this.onClick = config.onClick
    this.targetProgress = config.targetProgress || 1
    this.progress = config.progress || 0
  }

  /**
   * The current progress' fractional percentage (ie. 0 - 1).
   */
  get progressPercent(): number {
    return this.progress / this.targetProgress
  }

  get isCompleted(): boolean {
    return this.progress === this.targetProgress
  }

  /**
   * Add the given `progressToAdd` to the current `this.progress`
   * (up to `this.targetProgress`).
   */
  addProgress(progressToAdd: number): number {
    if (this.isCompleted) {
      return this.progress
    }

    this.progress += progressToAdd

    if (this.progress > this.targetProgress) {
      // Don't let progress go above `targetProgress`.
      this.progress = this.targetProgress
    }

    if (this.isCompleted) {
      this.onComplete()
    }

    this.checklist?.saveProgress()

    return this.progress
  }

  /**
   * Logic to run when this step is completed.
   */
  private onComplete(): void {
    Mixpanel.trackChecklistStepCompleted(this.id)

    const nextStep = this.checklist?.nextStep
    if (nextStep) {
      this.completionNotification(nextStep)
    } else {
      // If there's no next step, the checklist is completed.
      this.checklist?.complete()
    }
  }

  /**
   * Set the current progress (up to `this.targetProgress`).
   *
   * This method is meant to be used during initialization,
   * so it doesn't trigger the `onCompleted` hook.
   */
  setProgress(progress: number): void {
    this.progress = progress
    if (this.progress > this.targetProgress) {
      // Don't let progress go above `targetProgress`.
      this.progress = this.targetProgress
    }
  }

  /**
   * Create a notification for the completed step.
   */
  completionNotification(nextStep: ChecklistStepView): void {
    appNotifications.success({
      title: `Step completed: ${this.title}`,
      message: `Next up: ${nextStep.title}`,
      confetti: true,
    })
  }

  /**
   * Navigate to the given `path`, and show a notification with the `hintText`.
   */
  async navigateAndShowHint(path: string, hintText: string): Promise<void> {
    await router().push(path)
    setTimeout(() => {
      appNotifications.unique({
        title: this.title,
        message: hintText,
        duration: 30 * 1000,
      })
    })
  }
}

export class ChecklistView {
  readonly steps: ChecklistSteps
  /**
   * The user's account type at the moment they were assigned to the experiment.
   * This is used to determine the checklist steps, and for analytics.
   */
  readonly stratum: ChecklistStratum

  constructor(
    steps: ChecklistSteps,
    stratum: ChecklistStratum,
    progress?: ChecklistProgress,
  ) {
    this.steps = steps
    this.stratum = stratum

    // Set the relationship with all steps,
    // and set their progress.
    this.stepsArray.forEach((step) => {
      step.checklist = this
      if (progress) {
        const stepProgress = progress.steps[step.id]
        step.setProgress(stepProgress)
      }
    })

    // If this was initialized with no progress data, it means
    // the checklist is being initialized for the first time.
    // In this case, we create a new progress record in local storage
    // that will be used next time the checklist is initialized.
    if (!progress) {
      this.saveProgress()
    }
  }

  /**
   * Returns true when all steps in the checklist are completed.
   */
  get isCompleted(): boolean {
    return this.nextStep === undefined
  }

  /**
   * `this.steps` converted into an array.
   */
  get stepsArray(): ChecklistStepView[] {
    return Object.values(this.steps)
  }

  /**
   * Get the next incomplete step.
   */
  get nextStep(): ChecklistStepView | undefined {
    return this.stepsArray.find((step) => !step.isCompleted)
  }

  /**
   * Get an array of completed steps.
   */
  get completedSteps(): ChecklistStepView[] {
    return this.stepsArray.filter((step) => step.isCompleted)
  }

  /**
   * A fractional percentage (ie. 0 - 1) of the completed steps.
   */
  get progressPercent(): number {
    return this.completedSteps.length / this.stepsArray.length
  }

  /**
   * Return true if all the reading-related steps are completed.
   */
  get finishedReadingSteps(): boolean {
    return (
      this.steps.read_first.isCompleted && this.steps.read_second.isCompleted
    )
  }

  /**
   * Save the current progress.
   */
  saveProgress(): void {
    const progress = {
      is_completed: this.isCompleted,
      stratum: this.stratum,
      // Normalize `this.steps` into the `ChecklistProgressSteps`
      // interface (an object with the same keys, but with the
      // step's `progress` as the value).
      steps: this.stepsArray.reduce((result, step) => {
        result[step.id] = step.progress
        return result
      }, {} as ChecklistProgressSteps),
    }

    storage.setItem(
      StorageKeys.checklistProgress19055,
      JSON.stringify(progress),
    )

    backend.saveChecklistProgress19055(progress).catch((error) => {
      Sentry.captureException(error)
    })
  }

  /**
   * Mark the checklist as completed.
   */
  complete(): void {
    Mixpanel.trackChecklistCompleted()

    appNotifications.success({
      title: 'Checklist complete 🎉',
      message: "You're set up to get the most from Shortform",
      confetti: true,
    })
  }
}

/**
 * The global Checklist instance.
 *
 * We initialize it as `undefined` because it should only be instantiated
 * for users in the `B` variant of the `onboarding_checklist_19055` experiment.
 *
 * We access it via the `checklist()` function below to keep the original
 * `_checklist` variable private.
 *
 * To actually initialize it, we call `initChecklist()` at the appropriate
 * time (during the app initialization for users that are already assigned
 * to the `B` variant of the experiment, or right after starting the experiment).
 */
let _checklist: ChecklistView | undefined = undefined
export function checklist(): ChecklistView | undefined {
  return _checklist
}

/**
 * Initialize the global Checklist instance.
 */
export function initChecklist(
  stratum: ChecklistStratum,
  progress?: ChecklistProgress,
): void {
  const steps = getChecklistSteps(stratum)
  _checklist = new ChecklistView(steps, stratum, progress)
}

/**
 * Initialize the global Checklist instance from stored progress data.
 */
export async function resumeChecklist(): Promise<void> {
  const localProgress = storage.getItem(StorageKeys.checklistProgress19055)
  const parsedLocalProgress = localProgress
    ? (JSON.parse(localProgress) as ChecklistProgress)
    : undefined

  // If the checklist is already completed locally, there's no need to
  // query the current progress in the backend.
  if (parsedLocalProgress?.is_completed) {
    return initChecklist(parsedLocalProgress.stratum, parsedLocalProgress)
  }

  // Check for data stored in the backend. This is the main source of truth,
  // because it has to be synced between devices.
  const backendProgress = await backend.getChecklistProgress19055()
  if (backendProgress) {
    return initChecklist(backendProgress.stratum, backendProgress)
  }

  // This is an edge case: it should only happen if the user refreshed the page
  // before the request for creating a checklist record was fulfilled.
  if (parsedLocalProgress) {
    Sentry.captureMessage(
      'Tried to resume checklist with no progress in the DB',
    )
    return initChecklist(parsedLocalProgress.stratum, parsedLocalProgress)
  }

  // This should be an even edgier case: the user opened the app in another device
  // before the request was fulfilled, or the device doesn't support local storage.
  Sentry.captureMessage(
    'Tried to resume checklist with no progress locally or in the DB',
  )
}
